import { Injectable, inject } from '@angular/core';
import type { Stripe } from 'stripe';

import { HttpClient, HttpContext } from '@angular/common/http';
import { CustomerBillingPermission } from '@nusign/picflow-backend-types';
import { firstValueFrom, lastValueFrom } from 'rxjs';
import { freeScopes, freeStripeProductPrice, getBytesFromGigabytes } from '../billing-consts';
import { GbForStoragePrice, msInADay, trueUpDurationInDays } from '../consts';
import { getMetadataField } from '../lib/get-metadata-field';
import { TENANT_CONTEXT } from '../network-interceptor';
import { PICFLOW_CONFIG } from '../picflow-config';
import { PlanState } from '../types/plan-state.type';
import { Discount, Plan } from '../types/plan.type';
import { DataLoaderService } from './data-loader.service';
import { LoggedInAnalyticsService } from './loggedin-analytics.service';
import { ObservableDataLoaderService } from './observable-data-loader.service';
import { PermissionService } from './permission.service';
import { PricingService } from './pricing.service';
import { PromotionService } from './promotion.service';
import { StateService } from './state.service';

@Injectable({
  providedIn: 'root',
})
export class PlanService {
  private loggedInAnalytics = inject(LoggedInAnalyticsService);
  private config = inject(PICFLOW_CONFIG);
  private http = inject(HttpClient);
  private loader = inject(DataLoaderService);
  private observableDataLoader = inject(ObservableDataLoaderService);
  private state = inject(StateService);
  private promotionService = inject(PromotionService);
  private permissionService = inject(PermissionService);
  private pricingService = inject(PricingService);

  private findSubscriptionLineItem(
    items: Stripe.SubscriptionItem[],
    type: 'main' | 'seat_addon' | 'storage_addon'
  ): Stripe.SubscriptionItem {
    const item = items.find(item => {
      const itemType = (item.price.product as Stripe.Product).metadata.picflow_type;
      if (itemType === 'enterprise') return type === 'main'; // Enterprise is a main plan
      return itemType === type;
    });
    if (!item) {
      if (type !== 'main') return;
      console.error('Could not find price of type ' + type + ' in subscription items');
      return items[0];
    }
    return item;
  }

  private async getPlanPromo(stripeSub: Stripe.Subscription) {
    const promoCode = stripeSub.discount?.promotion_code as Stripe.PromotionCode;
    const currentPromoCode = promoCode?.code;
    if (currentPromoCode) {
      return await this.promotionService.checkCode(promoCode?.code);
    }

    if (stripeSub.discount) {
      // This is needed for when a promotion doesn't have a code
      return this.promotionService.convertPromo(stripeSub.discount as unknown as Stripe.PromotionCode);
    }
  }

  private async getSubscription() {
    const stripeSub = await lastValueFrom(
      this.http.get<Stripe.Subscription>(`${this.config.apiEndpoint}/billing/subscriptions`, {
        context: new HttpContext().set(TENANT_CONTEXT, this.state.activeTenant?.id),
      })
    );

    return {
      ...stripeSub,
      promo: await this.getPlanPromo(stripeSub),
    };
  }

  public async getPlan(): Promise<Plan> {
    this.loader.removeFuzzy('billingCustomer');
    this.loader.remove('plan-name_');

    if (this.observableDataLoader.getFromCacheFuzzy('galleries').length > 1) {
      // 1 means that just the shared galleries are loaded
      this.observableDataLoader.removeFuzzy('galleries');
    }
    this.loader.removeFuzzy('plan-name_');

    if (!this.state.activeTenant.id_stripe_subscription) {
      const storage = getBytesFromGigabytes(BigInt(freeStripeProductPrice.metadata.storage_capacity));
      const plan: Plan = {
        mainProductNameShort: 'free',
        latestInvoicePeriodEnd: undefined,
        status: 'active',
        subscriptionId: undefined,
        tenantId: this.state.activeTenant.id,
        storage: {
          total: storage,
          plan: storage,
          addOn: 0n,
        },
        scopes: freeScopes,
        price: freeStripeProductPrice,
        willRenew: true,
        includedSeatCount: 1,
        billingPeriodEnd: undefined,
        total: 0,
        subTotal: 0,
        hostedInvoiceUrl: undefined,
        isFree: true,
      };

      this.state.plan = plan;
      this.state.plan$.next(plan);
      if (this.state.user) this.loggedInAnalytics.identify();
      return plan;
    }

    const [stripeSub, permissions, mainPrices] = await Promise.all([
      this.getSubscription(),
      firstValueFrom(this.permissionService.getTenantBillingPermissions(this.state.activeTenant.id)),
      firstValueFrom(this.pricingService.getPrices('main')),
    ]);

    const planItem = this.findSubscriptionLineItem(stripeSub.items.data, 'main');

    const planPriceFromMainPrices = mainPrices.find(p => p.id === planItem.price.id);

    if (!planPriceFromMainPrices) {
      console.warn('Could not find price in main prices, using subscription price which may be outdated.');
    }

    const mainPrice = (planPriceFromMainPrices || planItem.price) as Stripe.Price & { product: Stripe.Product };
    const mainProduct = mainPrice.product;

    let mainProductNameShort = mainProduct.name;
    if (mainProductNameShort.includes('Picflow '))
      mainProductNameShort = mainProductNameShort.split('Picflow ')[1].toLowerCase();

    const storageAddon = this.findSubscriptionLineItem(stripeSub.items.data, 'storage_addon');

    let total = stripeSub.items.data.reduce((t, item) => t + item.price.unit_amount * item.quantity, 0);
    const subTotal = total;
    let discount: Discount;

    if (stripeSub.discount && stripeSub.promo) {
      discount = {
        start: stripeSub.discount.start * 1000,
        end: stripeSub.discount.end * 1000,
        promo: stripeSub.promo,
      };
      const discountAmount = discount.promo?.amountOff || (mainPrice.unit_amount / 100) * discount.promo.percentOff;
      if (this.promotionService.appliesToPrice(discount.promo, mainPrice)) {
        total -= discountAmount * 100;
      }
    }

    const isEnterprise = mainPrice.product.metadata.picflow_type === 'enterprise';
    const latestInvoice = stripeSub.latest_invoice as Stripe.Invoice;

    const planStorageTotal = BigInt(permissions.storage_plan_limit);
    const storageAddonTotal = storageAddon
      ? BigInt(GbForStoragePrice(storageAddon.price) * storageAddon.quantity) *
        BigInt(1024) *
        BigInt(1024) *
        BigInt(1024)
      : 0n;

    const includedSeatCount = Number(getMetadataField(mainPrice, 'included_seat_count'));

    const plan: Plan = {
      mainProductNameShort,
      latestInvoicePeriodEnd: latestInvoice?.period_end * 1000,
      status: stripeSub.status,
      subscriptionId: stripeSub.id,
      tenantId: this.state.activeTenant.id,
      storage: {
        total: planStorageTotal + storageAddonTotal,
        plan: planStorageTotal,
        addOn: storageAddonTotal,
      },
      scopes: permissions.scopes,
      includedSeatCount,
      price: mainPrice as Stripe.Price & { product: Stripe.Product },
      willRenew: !stripeSub.cancel_at_period_end,
      billingPeriodEnd: stripeSub.current_period_end * 1000,
      total,
      subTotal,
      storageAddon,
      trialEnd: stripeSub.trial_end * 1000,
      discount,
      // previousPromoCode: stripeSub.previousPromoCode,
      isEnterprise,
      isFree: false,
      hostedInvoiceUrl: latestInvoice?.hosted_invoice_url,
      seatAddon: this.findSubscriptionLineItem(stripeSub.items.data, 'seat_addon'),
      mainProductQuantity: planItem.quantity,
    };

    if (isEnterprise) {
      plan.nextTrueUpDate = this.getNextTrueUpPeriodEndDate(plan.billingPeriodEnd);
      const daysRemainingUntilTrueUpEnd = (plan.nextTrueUpDate - Date.now()) / msInADay;
      plan.inTrueUp = daysRemainingUntilTrueUpEnd < trueUpDurationInDays;
    }

    this.state.plan = plan;
    this.state.plan$.next(plan);

    if (this.state.user) this.loggedInAnalytics.identify();

    return plan;
  }

  public async getBillingCustomer(includeTaxId = false) {
    const key = 'billingCustomer' + (includeTaxId ? '_with-tax-id' : '');
    const customer = await this.loader.get(key, () =>
      lastValueFrom(
        this.http.get<{
          customer: Stripe.Customer;
          billing_permissions: CustomerBillingPermission;
        }>(`${this.config.apiEndpoint}/billing/customer${includeTaxId ? '?expand[]=tax_ids' : ''}`)
      )
    );
    return customer;
  }

  public async getBalance() {
    const customer = await this.getBillingCustomer();
    return -customer.customer.balance;
  }

  /**
   * Returns the next true up period end date based on the billing period end date
   */
  private getNextTrueUpPeriodEndDate(billingPeriodEndMs: number): number {
    const billingPeriodEndDate = new Date(billingPeriodEndMs);
    // removing three months from the billing period end date
    const monthsBetweenTrueUps = 3;
    const threeMonthsBefore = new Date(billingPeriodEndDate);
    threeMonthsBefore.setMonth(threeMonthsBefore.getMonth() - monthsBetweenTrueUps);
    // if the date is in the past, we return the billing period end date
    const dateIsInThePast = threeMonthsBefore.getTime() < Date.now();
    if (dateIsInThePast) return billingPeriodEndMs;
    // otherwise we recursively call this function with the new date
    return this.getNextTrueUpPeriodEndDate(threeMonthsBefore.getTime());
  }

  public getPlanState(): PlanState {
    if (this.state.plan.isEnterprise) return 'active';
    if (this.state.plan.price.unit_amount === 0) return 'free';
    if (this.state.plan.status === 'past_due' && !this.state.temporarilyHideUpgradeCta) return 'past-due';
    if (this.state.plan.status === 'trialing') {
      if (this.state.plan.willRenew) return 'trialing-will-renew';
      return 'trialing-wont-renew';
    }
    if (this.state.plan.willRenew) return 'active';
    return 'pending-termination';
  }
}
